import { MemberAvatar } from '@/components/MemberAvatar';
import {
  highlightMatch,
  stripHtml,
  type NoteRecord,
} from '@/components/NoteCard';
import { projectAccent } from '@/lib/deterministic-color';
import { cn } from '@/lib/utils';

const fmtTimeAgo = (minutes: number): string => {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;
  const days = Math.round(minutes / (60 * 24));
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
};

export interface NoteRowProps {
  note: NoteRecord;
  search: string;
  onOpen: (note: NoteRecord) => void;
}

/**
 * Notes redesign — rows layout. Direct port of the prototype's
 * `.v1-note-row` styling: dense table-like row with project rail,
 * single-line title + body excerpt, parent name, avatar, timestamp.
 *
 * Grid template:
 *   `12px   minmax(180px, 1fr)   minmax(140px, 0.6fr)   26px   110px`
 *      rail   title + body              parent              avatar  time
 *
 * Below 980px the parent column collapses (matches prototype's
 * `@media (max-width: 980px)` rule).
 */
export const NoteRow = ({ note, search, onOpen }: NoteRowProps) => {
  const isUntitled = note.title.trim().length === 0;
  const bodyText = stripHtml(note.body);
  const isEmpty = bodyText.length === 0;
  const railColor = projectAccent(note.railSeed);
  const parentLabel =
    note.parent.kind === 'task' ? note.parent.taskTitle : note.parent.projectName;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(note)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(note);
        }
      }}
      className={cn(
        'relative grid cursor-pointer items-center gap-x-[14px] border-b px-4 py-3 transition-colors duration-100',
        // Match the prototype's grid-template-columns. The 980px
        // breakpoint hides the parent + trailing pin columns.
        'grid-cols-[12px_minmax(180px,1fr)_minmax(140px,0.6fr)_26px_110px]',
        '[@media(max-width:980px)]:grid-cols-[12px_minmax(160px,1fr)_26px_90px]',
        'border-[var(--glass-card-border)] last:border-b-0',
        'hover:bg-[oklch(0_0_0/0.025)] dark:hover:bg-[oklch(1_0_0/0.04)]',
      )}
    >
      <span
        aria-hidden="true"
        className="h-[26px] w-[3px] rounded-full opacity-60"
        style={{ background: railColor }}
      />
      <div className="min-w-0">
        <div
          className={cn(
            'truncate text-[13px] font-medium tracking-[-0.005em] leading-[1.3]',
            isUntitled ? 'text-foreground-faint italic' : 'text-foreground',
          )}
        >
          {isUntitled ? 'Untitled' : highlightMatch(note.title, search)}
        </div>
        <div
          className={cn(
            'mt-0.5 truncate text-[11.5px]',
            isEmpty
              ? 'text-foreground-faint italic opacity-85'
              : 'text-foreground-faint',
          )}
        >
          {isEmpty ? 'No content yet.' : highlightMatch(bodyText, search)}
        </div>
      </div>
      {/* Parent column — hidden under 980px per the breakpoint above. */}
      <div
        className={cn(
          'inline-flex min-w-0 items-center gap-1.5 text-[11.5px]',
          'text-foreground-muted [@media(max-width:980px)]:hidden',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'size-2 flex-shrink-0',
            note.parent.kind === 'task' ? 'rounded-full' : 'rounded-[2px]',
          )}
          style={{ background: railColor }}
        />
        <span className="truncate">{parentLabel}</span>
      </div>
      <MemberAvatar
        id={note.authorId}
        name={note.authorName}
        email={note.authorEmail}
        size="xs"
        className="!size-5 !text-[8px]"
      />
      <span
        className={cn(
          'text-foreground-faint whitespace-nowrap text-right font-mono text-[11px] tabular-nums',
        )}
      >
        {note.edited ? `edited ${fmtTimeAgo(note.recencyMinutes)}` : fmtTimeAgo(note.recencyMinutes)}
      </span>
    </div>
  );
};
